import React, { useMemo, useState, useEffect } from "react";
import { Canvas, useLoader } from "@react-three/fiber";
import { OrbitControls, Text, Line } from "@react-three/drei";
import { TextureLoader } from "three";
import { Department, Landmark } from "../types";
import { findPath } from "../utils/pathfinding";

interface Map3DZonesProps {
  departments: Department[];
  landmarks: Landmark[];
  selectedDestination?: Department | Landmark | null;
  // Support both old kioskData and new startingPoint for backward compatibility
  kioskData?: any;
  startingPoint?: {
    id: number;
    poiName: string;
    points: string;
    longLatPoints: number[][];
  } | null;
}

interface Zone {
  id: number;
  name: string;
  points: string;
  floorID: number;
}

interface RoutingNode {
  id: number;
  x: number;
  y: number;
  type: number;
  isGate: boolean;
  isClosed: boolean;
  description: string;
}

interface POI {
  id: number;
  poiName: string;
  points: string;
  icon: string | null;
  iconWidth: number;
  iconHeight: number;
  longLatPoints: number[][];
  iconId: number | null;
  framedIconId: number;
  framedIconUrl: string;
}

// Parse points string "x1,y1#x2,y2#x3,y3" into array of [x, y]
function parsePoints(pointsStr: string): [number, number][] {
  return pointsStr.split("#").map((point) => {
    const [x, y] = point.split(",").map(Number);
    return [x, y];
  });
}

// Convert 2D pixel coordinates to 3D coordinates
function to3D(
  x: number,
  y: number,
  scale: number = 0.01,
): [number, number, number] {
  return [(x - 3350) * scale, 0, (y - 1300) * scale];
}

// Helper function to determine door configuration for zones
const getDoorConfig = (
  zoneName: string,
  wallIndex: number,
  points: [number, number][],
  scale: number,
) => {
  const name = zoneName.toLowerCase();

  // Pharmacy door on bottom wall
  if (name.includes("pharmacy")) {
    // Find the bottom wall (wall with highest Z coordinate - southernmost)
    const wallPositions = points.map((point, i) => {
      const start = points[i];
      const end = points[(i + 1) % points.length];
      const [, , z1] = to3D(start[0], start[1], scale);
      const [, , z2] = to3D(end[0], end[1], scale);
      return { wallIndex: i, avgZ: (z1 + z2) / 2 };
    });

    // Find wall with highest Z (bottom/south wall)
    const bottomWall = wallPositions.reduce((max, current) =>
      current.avgZ > max.avgZ ? current : max,
    );

    if (wallIndex === bottomWall.wallIndex) {
      return { hasDoor: true, doorWidth: 1.5, doorHeight: 2, isOpen: true };
    }
  }

  // Male Toilet door on right wall
  if (name.includes("male") && name.includes("toilet")) {
    // Find the right wall (wall with highest X coordinate - easternmost)
    const wallPositions = points.map((point, i) => {
      const start = points[i];
      const end = points[(i + 1) % points.length];
      const [x1, ,] = to3D(start[0], start[1], scale);
      const [x2, ,] = to3D(end[0], end[1], scale);
      return { wallIndex: i, avgX: (x1 + x2) / 2 };
    });

    // Find wall with highest X (right/east wall)
    const rightWall = wallPositions.reduce((max, current) =>
      current.avgX > max.avgX ? current : max,
    );

    if (wallIndex === rightWall.wallIndex) {
      return { hasDoor: true, doorWidth: 1.2, doorHeight: 2, isOpen: true };
    }
  }

  // Admission Office door on top wall (to face Pharmacy door)
  if (name.includes("admission")) {
    // Find the top wall (wall with lowest Z coordinate - northernmost)
    const wallPositions = points.map((point, i) => {
      const start = points[i];
      const end = points[(i + 1) % points.length];
      const [, , z1] = to3D(start[0], start[1], scale);
      const [, , z2] = to3D(end[0], end[1], scale);
      return { wallIndex: i, avgZ: (z1 + z2) / 2 };
    });

    // Find wall with lowest Z (top/north wall)
    const topWall = wallPositions.reduce((min, current) =>
      current.avgZ < min.avgZ ? current : min,
    );

    if (wallIndex === topWall.wallIndex) {
      return { hasDoor: true, doorWidth: 1.5, doorHeight: 2, isOpen: true };
    }
  }

  // Main Corridors - Add doors at specific ATM gate locations
  if (name.includes("main corridor")) {
    // Check each wall segment to see if it's near an ATM gate location
    const start = points[wallIndex];
    const end = points[(wallIndex + 1) % points.length];

    // SAB ATM - Gate 1: "257,1420#332,1420#332,1385#257,1385"
    // Admission Office: "258,1469#1052,1467#..."
    // Door should be on horizontal wall at y=1469, x between 257-332 (near Gate 1)

    // Check if wall is horizontal and at Gate 1/Admission intersection (y=1469, x around 257-332)
    const isHorizontalGate1Admission =
      Math.abs(start[1] - 1469) < 30 &&
      Math.abs(end[1] - 1469) < 30 &&
      Math.min(start[0], end[0]) < 332 &&
      Math.max(start[0], end[0]) > 257;

    // SAB ATM - Gate 5: "6367,1418#6469,1418#6469,1379#6367,1379"
    // Check if wall is vertical and at Gate 5 location (x=6367)
    const isVerticalGate5 =
      Math.abs(start[0] - 6367) < 20 &&
      Math.abs(end[0] - 6367) < 20 &&
      Math.min(start[1], end[1]) < 1418 &&
      Math.max(start[1], end[1]) > 1379;

    if (isHorizontalGate1Admission) {
      console.log(
        "🚪 Found Gate 1/Admission corridor door at wall",
        wallIndex,
        start,
        end,
      );
      return { hasDoor: true, doorWidth: 2.0, doorHeight: 2.5, isOpen: true };
    }

    if (isVerticalGate5) {
      console.log("🚪 Found Gate 5 door at wall", wallIndex, start, end);
      return { hasDoor: true, doorWidth: 2.0, doorHeight: 2.5, isOpen: true };
    }
  }

  return { hasDoor: false };
};

// Wall deduplication system
interface WallSegment {
  zoneId: number;
  zoneName: string;
  wallIndex: number;
  start: [number, number];
  end: [number, number];
  x1: number;
  z1: number;
  x2: number;
  z2: number;
  midX: number;
  midZ: number;
  length: number;
  angle: number;
  doorConfig: any;
}

const getZonePriority = (zoneName: string): number => {
  const name = zoneName.toLowerCase();
  // Higher priority zones will keep their walls when there's overlap
  if (name.includes("pharmacy")) return 9; // High priority to keep doors
  if (name.includes("toilet")) return 9;
  if (name.includes("emergency")) return 8;
  if (name.includes("respiratory")) return 7;
  if (name.includes("main corridor")) return 1; // Lowest priority - corridors get removed first
  if (name.includes("corridor")) return 2;
  return 5; // Default priority
};

const areWallsOverlapping = (
  wall1: WallSegment,
  wall2: WallSegment,
  tolerance: number = 0.5,
): boolean => {
  // Check if two walls are approximately the same (overlapping)
  const distance1 = Math.sqrt(
    Math.pow(wall1.x1 - wall2.x1, 2) + Math.pow(wall1.z1 - wall2.z1, 2),
  );
  const distance2 = Math.sqrt(
    Math.pow(wall1.x2 - wall2.x2, 2) + Math.pow(wall1.z2 - wall2.z2, 2),
  );
  const distance3 = Math.sqrt(
    Math.pow(wall1.x1 - wall2.x2, 2) + Math.pow(wall1.z1 - wall2.z2, 2),
  );
  const distance4 = Math.sqrt(
    Math.pow(wall1.x2 - wall2.x1, 2) + Math.pow(wall1.z2 - wall2.z1, 2),
  );

  // Walls overlap if their endpoints are very close (same wall or reverse direction)
  return (
    (distance1 < tolerance && distance2 < tolerance) ||
    (distance3 < tolerance && distance4 < tolerance)
  );
};

const deduplicateWalls = (allWalls: WallSegment[]): WallSegment[] => {
  const uniqueWalls: WallSegment[] = [];
  const processedWalls = new Set<number>();

  for (let i = 0; i < allWalls.length; i++) {
    if (processedWalls.has(i)) continue;

    const currentWall = allWalls[i];
    let keepCurrentWall = true;

    // Check against all other walls
    for (let j = i + 1; j < allWalls.length; j++) {
      if (processedWalls.has(j)) continue;

      const otherWall = allWalls[j];

      if (areWallsOverlapping(currentWall, otherWall)) {
        // Walls overlap - keep the one with higher priority
        const currentPriority = getZonePriority(currentWall.zoneName);
        const otherPriority = getZonePriority(otherWall.zoneName);

        if (otherPriority > currentPriority) {
          // Other wall has higher priority, skip current wall
          keepCurrentWall = false;
          break;
        } else {
          // Current wall has higher priority, mark other wall as processed
          processedWalls.add(j);
        }
      }
    }

    if (keepCurrentWall) {
      uniqueWalls.push(currentWall);
    }
    processedWalls.add(i);
  }

  return uniqueWalls;
};

// Unified Wall System - Processes all zones and removes overlapping walls
const UnifiedWallSystem: React.FC<{ zones: Zone[]; scale: number }> = ({
  zones,
  scale,
}) => {
  const wallSegments = useMemo(() => {
    // Step 1: Generate all wall segments from all zones
    const allWalls: WallSegment[] = [];

    zones.forEach((zone) => {
      const points = parsePoints(zone.points);

      for (let i = 0; i < points.length; i++) {
        const start = points[i];
        const end = points[(i + 1) % points.length];

        const [x1, , z1] = to3D(start[0], start[1], scale);
        const [x2, , z2] = to3D(end[0], end[1], scale);

        const midX = (x1 + x2) / 2;
        const midZ = (z1 + z2) / 2;
        const length = Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(z2 - z1, 2));
        const angle = Math.atan2(z2 - z1, x2 - x1);

        const doorConfig = getDoorConfig(zone.name, i, points, scale);

        allWalls.push({
          zoneId: zone.id,
          zoneName: zone.name,
          wallIndex: i,
          start,
          end,
          x1,
          z1,
          x2,
          z2,
          midX,
          midZ,
          length,
          angle,
          doorConfig,
        });
      }
    });

    // Step 2: Remove overlapping walls
    const uniqueWalls = deduplicateWalls(allWalls);

    console.log(
      `🏗️ Wall deduplication: ${allWalls.length} → ${uniqueWalls.length} walls (removed ${allWalls.length - uniqueWalls.length} overlapping walls)`,
    );

    // Step 3: Generate JSX elements for unique walls
    const wallElements: JSX.Element[] = [];

    uniqueWalls.forEach((wall, index) => {
      if (wall.doorConfig.hasDoor && wall.length > wall.doorConfig.doorWidth!) {
        // Create wall with door opening
        const doorWidth = wall.doorConfig.doorWidth!;
        const doorHeight = wall.doorConfig.doorHeight!;
        const wallHeight = 1.5;
        const wallThickness = 0.1;

        // Left wall segment
        const leftSegmentLength = (wall.length - doorWidth) / 2;
        wallElements.push(
          <mesh
            key={`wall-left-${wall.zoneId}-${wall.wallIndex}-${index}`}
            position={[
              wall.midX -
                (doorWidth / 2 + leftSegmentLength / 2) * Math.cos(wall.angle),
              1,
              wall.midZ -
                (doorWidth / 2 + leftSegmentLength / 2) * Math.sin(wall.angle),
            ]}
            rotation={[0, -wall.angle, 0]}
            castShadow
            receiveShadow
          >
            <boxGeometry
              args={[leftSegmentLength, wallHeight, wallThickness]}
            />
            <meshStandardMaterial
              color={getZoneColor(wall.zoneName)}
              transparent
              opacity={0.7}
              roughness={0.8}
            />
          </mesh>,
        );

        // Right wall segment
        wallElements.push(
          <mesh
            key={`wall-right-${wall.zoneId}-${wall.wallIndex}-${index}`}
            position={[
              wall.midX +
                (doorWidth / 2 + leftSegmentLength / 2) * Math.cos(wall.angle),
              1,
              wall.midZ +
                (doorWidth / 2 + leftSegmentLength / 2) * Math.sin(wall.angle),
            ]}
            rotation={[0, -wall.angle, 0]}
            castShadow
            receiveShadow
          >
            <boxGeometry
              args={[leftSegmentLength, wallHeight, wallThickness]}
            />
            <meshStandardMaterial
              color={getZoneColor(wall.zoneName)}
              transparent
              opacity={0.7}
              roughness={0.8}
            />
          </mesh>,
        );

        // Top wall segment above door
        wallElements.push(
          <mesh
            key={`wall-top-${wall.zoneId}-${wall.wallIndex}-${index}`}
            position={[
              wall.midX,
              1 + doorHeight / 2 + (wallHeight - doorHeight) / 2,
              wall.midZ,
            ]}
            rotation={[0, -wall.angle, 0]}
            castShadow
            receiveShadow
          >
            <boxGeometry
              args={[doorWidth, wallHeight - doorHeight, wallThickness]}
            />
            <meshStandardMaterial
              color={getZoneColor(wall.zoneName)}
              transparent
              opacity={0.7}
              roughness={0.8}
            />
          </mesh>,
        );

        // Door frame
        wallElements.push(
          <group key={`door-frame-${wall.zoneId}-${wall.wallIndex}-${index}`}>
            {/* Door frame - left */}
            <mesh
              position={[
                wall.midX - (doorWidth / 2) * Math.cos(wall.angle),
                doorHeight / 2,
                wall.midZ - (doorWidth / 2) * Math.sin(wall.angle),
              ]}
            >
              <boxGeometry args={[0.1, doorHeight, 0.15]} />
              <meshStandardMaterial color="#8b4513" />
            </mesh>
            {/* Door frame - right */}
            <mesh
              position={[
                wall.midX + (doorWidth / 2) * Math.cos(wall.angle),
                doorHeight / 2,
                wall.midZ + (doorWidth / 2) * Math.sin(wall.angle),
              ]}
            >
              <boxGeometry args={[0.1, doorHeight, 0.15]} />
              <meshStandardMaterial color="#8b4513" />
            </mesh>
            {/* Door frame - top */}
            <mesh
              position={[wall.midX, doorHeight, wall.midZ]}
              rotation={[0, -wall.angle, 0]}
            >
              <boxGeometry args={[doorWidth, 0.1, 0.15]} />
              <meshStandardMaterial color="#8b4513" />
            </mesh>
          </group>,
        );
      } else {
        // Regular wall without door
        wallElements.push(
          <mesh
            key={`wall-${wall.zoneId}-${wall.wallIndex}-${index}`}
            position={[wall.midX, 1, wall.midZ]}
            rotation={[0, -wall.angle, 0]}
            castShadow
            receiveShadow
          >
            <boxGeometry args={[wall.length, 1.5, 0.1]} />
            <meshStandardMaterial
              color={getZoneColor(wall.zoneName)}
              transparent
              opacity={0.7}
              roughness={0.8}
            />
          </mesh>,
        );
      }
    });

    return wallElements;
  }, [zones, scale]);

  return <group>{wallSegments}</group>;
};

// Zone Room Component - Now simplified to just render zone labels
const ZoneRoom: React.FC<{ zone: Zone; scale: number }> = ({ zone, scale }) => {
  // This component now only handles zone-specific elements like labels
  // Walls are handled by UnifiedWallSystem to avoid overlaps
  return null; // Walls moved to UnifiedWallSystem
};

// Floor Plan Base Layer Component
const FloorPlanBase: React.FC<{ scale: number }> = ({ scale }) => {
  try {
    const texture = useLoader(TextureLoader, "/ground-floor-layout.png");

    // Use actual PNG file dimensions
    // PNG file size: 6568x2456 pixels
    const coordinateWidth = 6568; // Actual PNG width
    const coordinateHeight = 2456; // Actual PNG height

    // Apply the same scale as used in to3D function
    const planeWidth = coordinateWidth * scale;
    const planeHeight = coordinateHeight * scale;

    return (
      <mesh
        rotation={[-Math.PI / 2, 0, 0]}
        position={[-0.6, -0.01, -0.7]}
        receiveShadow
      >
        <planeGeometry args={[planeWidth, planeHeight]} />
        <meshBasicMaterial map={texture} color="#ffffff" toneMapped={false} />
      </mesh>
    );
  } catch (error) {
    console.error("Failed to load floor plan texture:", error);
    // Return a simple gray plane as fallback with correct PNG dimensions
    const coordinateWidth = 6568;
    const coordinateHeight = 2456;
    const planeWidth = coordinateWidth * 0.01;
    const planeHeight = coordinateHeight * 0.01;

    return (
      <mesh
        rotation={[-Math.PI / 2, 0, 0]}
        position={[0, -0.01, 0]}
        receiveShadow
      >
        <planeGeometry args={[planeWidth, planeHeight]} />
        <meshBasicMaterial color="#ffffff" transparent opacity={0.3} />
      </mesh>
    );
  }
};

// POI Icon Component - Creates 3D icons for different POI types
const POIIcon: React.FC<{ poi: POI; scale: number }> = ({ poi, scale }) => {
  const points = useMemo(() => parsePoints(poi.points), [poi.points]);

  // Calculate POI center from points
  const centerX = points.reduce((sum, p) => sum + p[0], 0) / points.length;
  const centerY = points.reduce((sum, p) => sum + p[1], 0) / points.length;
  const [x, , z] = to3D(centerX, centerY, scale);

  // Get icon type and color based on POI name
  const getIconType = (name: string) => {
    const lowerName = name.toLowerCase();
    if (lowerName.includes("pharmacy"))
      return { type: "pharmacy", color: "#10b981" };
    if (lowerName.includes("toilet"))
      return { type: "toilet", color: "#6b7280" };
    if (lowerName.includes("coffee") || lowerName.includes("saldwich"))
      return { type: "food", color: "#f59e0b" };
    if (lowerName.includes("atm")) return { type: "atm", color: "#3b82f6" };
    if (lowerName.includes("admission") || lowerName.includes("information"))
      return { type: "info", color: "#8b5cf6" };
    if (
      lowerName.includes("clinic") ||
      lowerName.includes("emergency") ||
      lowerName.includes("er")
    )
      return { type: "medical", color: "#ef4444" };
    if (lowerName.includes("retail"))
      return { type: "retail", color: "#f97316" };
    return { type: "general", color: "#64748b" };
  };

  const { type, color } = getIconType(poi.poiName);

  return (
    <group position={[x, 0, z]}>
      {/* Pharmacy Symbol - Cross */}
      {type === "pharmacy" && (
        <group position={[0, 2, 0]}>
          <mesh position={[0, 0, 0]}>
            <boxGeometry args={[0.6, 0.2, 0.2]} />
            <meshStandardMaterial color={color} />
          </mesh>
          <mesh position={[0, 0, 0]}>
            <boxGeometry args={[0.2, 0.6, 0.2]} />
            <meshStandardMaterial color={color} />
          </mesh>
        </group>
      )}

      {/* Toilet Symbol - Human figure */}
      {type === "toilet" && (
        <group position={[0, 2, 0]}>
          {/* Head */}
          <mesh position={[0, 0.4, 0]}>
            <sphereGeometry args={[0.15, 8, 8]} />
            <meshStandardMaterial color={color} />
          </mesh>
          {/* Body */}
          <mesh position={[0, 0, 0]}>
            <boxGeometry args={[0.3, 0.6, 0.15]} />
            <meshStandardMaterial color={color} />
          </mesh>
          {/* Legs */}
          <mesh position={[-0.08, -0.4, 0]}>
            <boxGeometry args={[0.12, 0.4, 0.15]} />
            <meshStandardMaterial color={color} />
          </mesh>
          <mesh position={[0.08, -0.4, 0]}>
            <boxGeometry args={[0.12, 0.4, 0.15]} />
            <meshStandardMaterial color={color} />
          </mesh>
        </group>
      )}

      {/* Food Symbol - Coffee cup */}
      {type === "food" && (
        <group position={[0, 2, 0]}>
          {/* Cup */}
          <mesh position={[0, 0, 0]}>
            <cylinderGeometry args={[0.25, 0.2, 0.5, 8]} />
            <meshStandardMaterial color={color} />
          </mesh>
          {/* Handle */}
          <mesh position={[0.3, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
            <torusGeometry args={[0.15, 0.04, 8, 16]} />
            <meshStandardMaterial color={color} />
          </mesh>
          {/* Steam */}
          <mesh position={[0, 0.4, 0]}>
            <sphereGeometry args={[0.06, 8, 8]} />
            <meshStandardMaterial color={color} />
          </mesh>
        </group>
      )}

      {/* ATM Symbol - ATM machine */}
      {type === "atm" && (
        <group position={[0, 2, 0]}>
          {/* ATM Screen */}
          <mesh position={[0, 0.1, 0]}>
            <boxGeometry args={[0.5, 0.4, 0.15]} />
            <meshStandardMaterial color={color} />
          </mesh>
          {/* Screen display */}
          <mesh position={[0, 0.1, 0.08]}>
            <boxGeometry args={[0.35, 0.25, 0.02]} />
            <meshStandardMaterial color="#000000" />
          </mesh>
          {/* Card slot */}
          <mesh position={[0, -0.15, 0.08]}>
            <boxGeometry args={[0.4, 0.05, 0.02]} />
            <meshStandardMaterial color="#000000" />
          </mesh>
          {/* Keypad */}
          <mesh position={[0, -0.3, 0]}>
            <boxGeometry args={[0.25, 0.2, 0.1]} />
            <meshStandardMaterial color={color} />
          </mesh>
        </group>
      )}

      {/* Information Symbol - "i" in circle */}
      {type === "info" && (
        <group position={[0, 2, 0]}>
          {/* Circle background */}
          <mesh position={[0, 0, 0]}>
            <cylinderGeometry args={[0.3, 0.3, 0.08]} />
            <meshStandardMaterial color={color} />
          </mesh>
          {/* "i" dot */}
          <mesh position={[0, 0.15, 0.05]}>
            <sphereGeometry args={[0.05, 8, 8]} />
            <meshStandardMaterial color="#ffffff" />
          </mesh>
          {/* "i" line */}
          <mesh position={[0, -0.05, 0.05]}>
            <boxGeometry args={[0.06, 0.25, 0.06]} />
            <meshStandardMaterial color="#ffffff" />
          </mesh>
        </group>
      )}

      {/* Medical Symbol - Red Cross */}
      {type === "medical" && (
        <group position={[0, 2, 0]}>
          <mesh position={[0, 0, 0]}>
            <boxGeometry args={[0.6, 0.2, 0.2]} />
            <meshStandardMaterial color={color} />
          </mesh>
          <mesh position={[0, 0, 0]}>
            <boxGeometry args={[0.2, 0.6, 0.2]} />
            <meshStandardMaterial color={color} />
          </mesh>
        </group>
      )}

      {/* General Symbol - Simple cube */}
      {type === "general" && (
        <mesh position={[0, 2, 0]}>
          <boxGeometry args={[0.4, 0.4, 0.4]} />
          <meshStandardMaterial color={color} />
        </mesh>
      )}

      {/* POI label with blue color and larger font */}
      <Text
        position={[0, 3.5, 0]}
        fontSize={0.5}
        color="#0000ff"
        anchorX="center"
        anchorY="middle"
        maxWidth={poi.poiName.length * 0.6}
      >
        {poi.poiName}
      </Text>
    </group>
  );
};

// Zone Label Component - Adds flat 2D labels on the floor for each zone
const ZoneLabel: React.FC<{ zone: Zone; scale: number }> = ({
  zone,
  scale,
}) => {
  const points = useMemo(() => parsePoints(zone.points), [zone.points]);

  // Calculate zone center
  const centerX = points.reduce((sum, p) => sum + p[0], 0) / points.length;
  const centerY = points.reduce((sum, p) => sum + p[1], 0) / points.length;
  let [x, , z] = to3D(centerX, centerY, scale);

  // Skip labels for very small zones or main corridors (too cluttered)
  if (zone.name.toLowerCase().includes("main corridor")) {
    return null;
  }

  // Manual position adjustments for specific zones
  const zoneName = zone.name.toLowerCase();
  if (zoneName.includes("admission office")) {
    // Move Admission Office label left and top (negative X, positive Z)
    x -= 3; // Move left
    z -= 2; // Move forward/top
  } else if (zoneName === "er") {
    // Move ER label slightly to avoid overlap
    x += 2; // Move right
    z -= 0; // Move back
  } else if (zoneName.includes("pharmacy")) {
    // Adjust pharmacy labels
    x -= 1; // Move slightly right
    z += 0; // Move slightly forward
  } else if (zoneName.includes("respiratory")) {
    // Move Respiratory Pre Care label
    x -= 0.5; // Move left
    z += 1; // Move forward
  } else if (zoneName.includes("emergency dep-1")) {
    // Move Emergency Dep-1 label
    x += 1; // Move left
    z += 0; // Move back
  } else if (zoneName.includes("emergency dep-2")) {
    // Move Emergency Dep-2 label
    x += 0; // Move right
    z -= 1; // Move back
  } else if (zoneName.includes("emergency lobby")) {
    // Move Emergency Lobby label
    x -= 1; // Move right
    z += 0; // Move forward
  } else if (zoneName.includes("er main entrance")) {
    // Move ER Main Entrance label
    x += 0.5; // Move left
    z -= 2; // Move back
  } else if (zoneName.includes("er entrance")) {
    // Move ER Entrance label
    x += 2; // Move right
    z -= 2; // Move back
  }

  return (
    <group position={[x, 0.1, z]}>
      {/* Black background plane */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
        <planeGeometry args={[zone.name.length * 0.3, 0.8]} />
        <meshBasicMaterial color="#000000" transparent opacity={0.8} />
      </mesh>

      {/* White text label on black background */}
      <Text
        position={[0, 0.01, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
        fontSize={0.5}
        color="#ffffff"
        anchorX="center"
        anchorY="middle"
        maxWidth={zone.name.length * 0.3}
      >
        {zone.name}
      </Text>
    </group>
  );
};

// Get color based on zone name
function getZoneColor(zoneName: string): string {
  // All walls in lighter grey
  return "#e5e7eb"; // Lighter grey color
}

// Pulsing Destination Marker Component
const PulsingDestinationMarker: React.FC = () => {
  const meshRef = React.useRef<any>();
  const outerRingRef = React.useRef<any>();
  const animationIdRef = React.useRef<number>();

  React.useEffect(() => {
    if (!meshRef.current || !outerRingRef.current) return;

    const animate = () => {
      // Check if refs are still valid before accessing them
      if (!meshRef.current || !outerRingRef.current) return;

      const time = Date.now() * 0.003;

      // Pulsing scale animation with safe access
      const scale = 1 + Math.sin(time * 2) * 0.3;
      if (meshRef.current && meshRef.current.scale) {
        meshRef.current.scale.setScalar(scale);
      }

      // Outer ring pulsing with safe access
      const outerScale = 1 + Math.sin(time * 1.5) * 0.5;
      if (outerRingRef.current && outerRingRef.current.scale) {
        outerRingRef.current.scale.setScalar(outerScale);
      }

      // Pulsing opacity
      const opacity = 0.6 + Math.sin(time * 3) * 0.4;
      if (meshRef.current && meshRef.current.material) {
        meshRef.current.material.opacity = opacity;
      }
      if (outerRingRef.current && outerRingRef.current.material) {
        outerRingRef.current.material.opacity = opacity * 0.5;
      }

      animationIdRef.current = requestAnimationFrame(animate);
    };

    animate();

    // Cleanup function to cancel animation when component unmounts
    return () => {
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
    };
  }, []);

  return (
    <group position={[0, 4, 0]}>
      {/* Main red pulsing ring */}
      <mesh ref={meshRef} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[0.6, 1.0, 16]} />
        <meshStandardMaterial
          color="#ef4444"
          emissive="#ef4444"
          emissiveIntensity={1.2}
          transparent
          opacity={0.8}
        />
      </mesh>

      {/* Outer pulsing ring */}
      <mesh ref={outerRingRef} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[1.0, 1.5, 16]} />
        <meshStandardMaterial
          color="#dc2626"
          emissive="#dc2626"
          emissiveIntensity={0.8}
          transparent
          opacity={0.4}
        />
      </mesh>

      {/* Central glowing dot */}
      <mesh position={[0, 0.1, 0]}>
        <sphereGeometry args={[0.2, 8, 8]} />
        <meshStandardMaterial
          color="#fca5a5"
          emissive="#ef4444"
          emissiveIntensity={1.5}
        />
      </mesh>
    </group>
  );
};
const AnimatedRoutePath: React.FC<{
  path: { x: number; y: number }[];
  scale: number;
}> = ({ path, scale }) => {
  const points = useMemo(() => {
    return path.map((p) => to3D(p.x, p.y, scale));
  }, [path, scale]);

  if (points.length < 2) return null;

  return (
    <group>
      {/* Main route line segments with enhanced swimming animation */}
      {points.slice(0, -1).map((point, i) => {
        const nextPoint = points[i + 1];

        // Calculate direction vector
        const direction = [nextPoint[0] - point[0], 0, nextPoint[2] - point[2]];
        const distance = Math.sqrt(direction[0] ** 2 + direction[2] ** 2);

        // Calculate rotation angle
        const angle = Math.atan2(direction[2], direction[0]);

        return (
          <group key={`segment-${i}`}>
            {/* Base route line */}
            <Line
              points={[
                [point[0], 0.5, point[2]],
                [nextPoint[0], 0.5, nextPoint[2]],
              ]}
              color="#3b82f6"
              lineWidth={4}
            />

            {/* Enhanced swimming/flowing animation particles */}
            <SwimmingParticles
              startPoint={point as [number, number, number]}
              endPoint={nextPoint as [number, number, number]}
              segmentIndex={i}
              distance={distance}
            />

            {/* Directional arrow at segment midpoint */}
            <group
              position={[
                (point[0] + nextPoint[0]) / 2,
                0.6,
                (point[2] + nextPoint[2]) / 2,
              ]}
            >
              <mesh rotation={[0, angle, 0]}>
                <coneGeometry args={[0.12, 0.4, 6]} />
                <meshStandardMaterial
                  color="#1d4ed8"
                  emissive="#1d4ed8"
                  emissiveIntensity={0.4}
                />
              </mesh>
            </group>
          </group>
        );
      })}

      {/* Waypoint markers with subtle pulsing */}
      {points.map((point, i) => (
        <group key={`waypoint-${i}`} position={[point[0], 0.5, point[2]]}>
          <mesh>
            <sphereGeometry args={[0.1, 8, 8]} />
            <meshStandardMaterial
              color="#3b82f6"
              emissive="#3b82f6"
              emissiveIntensity={0.3}
            />
          </mesh>
        </group>
      ))}
    </group>
  );
};

// Enhanced swimming particles component for fluid animation
const SwimmingParticles: React.FC<{
  startPoint: [number, number, number];
  endPoint: [number, number, number];
  segmentIndex: number;
  distance: number;
}> = ({ startPoint, endPoint, segmentIndex, distance }) => {
  // Create more particles for longer segments
  const particleCount = Math.max(3, Math.min(8, Math.floor(distance / 2)));
  const particles = Array.from({ length: particleCount }, (_, i) => i);

  return (
    <group>
      {particles.map((particleIndex) => (
        <SwimmingParticle
          key={`swimming-particle-${segmentIndex}-${particleIndex}`}
          startPoint={startPoint}
          endPoint={endPoint}
          delay={particleIndex * 0.3 + segmentIndex * 0.1}
          particleIndex={particleIndex}
          totalParticles={particleCount}
        />
      ))}
    </group>
  );
};

// Individual swimming particle with wave motion
const SwimmingParticle: React.FC<{
  startPoint: [number, number, number];
  endPoint: [number, number, number];
  delay: number;
  particleIndex: number;
  totalParticles: number;
}> = ({ startPoint, endPoint, delay, particleIndex, totalParticles }) => {
  const meshRef = React.useRef<any>();
  const trailRef = React.useRef<any>();
  const animationIdRef = React.useRef<number>();

  React.useEffect(() => {
    if (!meshRef.current) return;

    const animate = () => {
      // Check if refs are still valid before accessing them
      if (!meshRef.current || !trailRef.current) return;

      const time = (Date.now() * 0.001 + delay) % 3; // 3 second cycle
      const progress = time / 3; // Linear progress from 0 to 1

      // Interpolate position with swimming wave motion
      const baseProgress = progress;
      const waveOffset = Math.sin(progress * Math.PI * 4 + particleIndex) * 0.1;

      meshRef.current.position.x =
        startPoint[0] + (endPoint[0] - startPoint[0]) * baseProgress;
      meshRef.current.position.y =
        startPoint[1] +
        (endPoint[1] - startPoint[1]) * baseProgress +
        0.2 +
        waveOffset;
      meshRef.current.position.z =
        startPoint[2] + (endPoint[2] - startPoint[2]) * baseProgress;

      // Swimming-like opacity and size variation
      const opacity = Math.sin(progress * Math.PI * 2) * 0.4 + 0.6;
      const scale =
        Math.sin(progress * Math.PI * 3 + particleIndex) * 0.3 + 0.7;

      // Safe access to material and scale properties
      if (meshRef.current && meshRef.current.material) {
        meshRef.current.material.opacity = opacity;
      }

      if (meshRef.current && meshRef.current.scale) {
        meshRef.current.scale.setScalar(scale);
      }

      // Trail effect with safe access
      if (trailRef.current && trailRef.current.material) {
        trailRef.current.material.opacity = opacity * 0.3;
      }

      if (trailRef.current && trailRef.current.scale) {
        trailRef.current.scale.setScalar(scale * 1.5);
      }

      animationIdRef.current = requestAnimationFrame(animate);
    };

    animate();

    // Cleanup function to cancel animation when component unmounts
    return () => {
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
    };
  }, [startPoint, endPoint, delay, particleIndex]);

  return (
    <group>
      {/* Main particle */}
      <mesh ref={meshRef}>
        <sphereGeometry args={[0.06, 8, 8]} />
        <meshStandardMaterial
          color="#60a5fa"
          transparent
          opacity={0.8}
          emissive="#60a5fa"
          emissiveIntensity={0.7}
        />
      </mesh>

      {/* Trail effect */}
      <mesh ref={trailRef}>
        <sphereGeometry args={[0.04, 6, 6]} />
        <meshStandardMaterial
          color="#93c5fd"
          transparent
          opacity={0.3}
          emissive="#93c5fd"
          emissiveIntensity={0.4}
        />
      </mesh>
    </group>
  );
};

// Furniture Components for Z10 Zone
const Chair: React.FC<{
  position: [number, number, number];
  rotation?: number;
}> = ({ position, rotation = 0 }) => {
  return (
    <group position={position} rotation={[0, rotation, 0]}>
      {/* Seat */}
      <mesh position={[0, 0.25, 0]}>
        <boxGeometry args={[0.4, 0.05, 0.4]} />
        <meshStandardMaterial color="#8b4513" />
      </mesh>
      {/* Backrest */}
      <mesh position={[0, 0.5, -0.175]}>
        <boxGeometry args={[0.4, 0.5, 0.05]} />
        <meshStandardMaterial color="#8b4513" />
      </mesh>
      {/* Legs */}
      {[
        [-0.15, -0.15],
        [0.15, -0.15],
        [-0.15, 0.15],
        [0.15, 0.15],
      ].map((offset, i) => (
        <mesh key={i} position={[offset[0], 0.125, offset[1]]}>
          <cylinderGeometry args={[0.02, 0.02, 0.25, 8]} />
          <meshStandardMaterial color="#654321" />
        </mesh>
      ))}
    </group>
  );
};

const Table: React.FC<{ position: [number, number, number] }> = ({
  position,
}) => {
  return (
    <group position={position}>
      {/* Table top */}
      <mesh position={[0, 0.5, 0]}>
        <boxGeometry args={[1.2, 0.05, 0.8]} />
        <meshStandardMaterial color="#a0522d" />
      </mesh>
      {/* Legs */}
      {[
        [-0.5, -0.3],
        [0.5, -0.3],
        [-0.5, 0.3],
        [0.5, 0.3],
      ].map((offset, i) => (
        <mesh key={i} position={[offset[0], 0.25, offset[1]]}>
          <cylinderGeometry args={[0.04, 0.04, 0.5, 8]} />
          <meshStandardMaterial color="#654321" />
        </mesh>
      ))}
    </group>
  );
};

const Bed: React.FC<{
  position: [number, number, number];
  rotation?: number;
}> = ({ position, rotation = 0 }) => {
  return (
    <group position={position} rotation={[0, rotation, 0]}>
      {/* Mattress */}
      <mesh position={[0, 0.35, 0]}>
        <boxGeometry args={[0.9, 0.15, 1.8]} />
        <meshStandardMaterial color="#e8e8e8" />
      </mesh>
      {/* Bed frame */}
      <mesh position={[0, 0.2, 0]}>
        <boxGeometry args={[1.0, 0.1, 1.9]} />
        <meshStandardMaterial color="#8b4513" />
      </mesh>
      {/* Headboard */}
      <mesh position={[0, 0.6, -0.9]}>
        <boxGeometry args={[1.0, 0.6, 0.1]} />
        <meshStandardMaterial color="#654321" />
      </mesh>
      {/* Legs */}
      {[
        [-0.4, -0.85],
        [0.4, -0.85],
        [-0.4, 0.85],
        [0.4, 0.85],
      ].map((offset, i) => (
        <mesh key={i} position={[offset[0], 0.1, offset[1]]}>
          <cylinderGeometry args={[0.03, 0.03, 0.2, 8]} />
          <meshStandardMaterial color="#654321" />
        </mesh>
      ))}
    </group>
  );
};

// Z10 Furniture Layout Component
const Z10Furniture: React.FC<{ scale: number }> = ({ scale }) => {
  // Z10 zone points: "2946,1486#2786,1485#2786,1530#2364,1532#2365,1486#2176,1486#2177,1621#2252,1621#2251,1919#2205,1920#2204,2228#2947,2228"
  // Calculate approximate center and boundaries
  const z10Points = parsePoints(
    "2946,1486#2786,1485#2786,1530#2364,1532#2365,1486#2176,1486#2177,1621#2252,1621#2251,1919#2205,1920#2204,2228#2947,2228",
  );

  // Calculate center
  const centerX =
    z10Points.reduce((sum, p) => sum + p[0], 0) / z10Points.length;
  const centerY =
    z10Points.reduce((sum, p) => sum + p[1], 0) / z10Points.length;
  const [baseX, , baseZ] = to3D(centerX, centerY, scale);

  return (
    <group>
      {/* Row 1: Beds on the left side */}
      <Bed position={[baseX - 3, 0, baseZ - 2]} rotation={Math.PI / 2} />
      <Bed position={[baseX - 3, 0, baseZ]} rotation={Math.PI / 2} />
      <Bed position={[baseX - 3, 0, baseZ + 2]} rotation={Math.PI / 2} />

      {/* Row 2: Tables and chairs in the middle */}
      <Table position={[baseX, 0, baseZ - 2]} />
      <Chair position={[baseX - 0.7, 0, baseZ - 2]} rotation={Math.PI / 2} />
      <Chair position={[baseX + 0.7, 0, baseZ - 2]} rotation={-Math.PI / 2} />

      <Table position={[baseX, 0, baseZ + 2]} />
      <Chair position={[baseX - 0.7, 0, baseZ + 2]} rotation={Math.PI / 2} />
      <Chair position={[baseX + 0.7, 0, baseZ + 2]} rotation={-Math.PI / 2} />

      {/* Row 3: More beds on the right side */}
      <Bed position={[baseX + 3, 0, baseZ - 2]} rotation={-Math.PI / 2} />
      <Bed position={[baseX + 3, 0, baseZ]} rotation={-Math.PI / 2} />
      <Bed position={[baseX + 3, 0, baseZ + 2]} rotation={-Math.PI / 2} />

      {/* Additional chairs scattered around */}
      <Chair position={[baseX - 1.5, 0, baseZ]} />
      <Chair position={[baseX + 1.5, 0, baseZ]} rotation={Math.PI} />
    </group>
  );
};

// Main Component
const Map3DZones: React.FC<Map3DZonesProps> = ({
  departments,
  landmarks,
  selectedDestination,
  kioskData,
  startingPoint,
}) => {
  const [zones, setZones] = useState<Zone[]>([]);
  const [routingNodes, setRoutingNodes] = useState<RoutingNode[]>([]);
  const [pois, setPois] = useState<POI[]>([]);
  const [computedPath, setComputedPath] = useState<{ x: number; y: number }[]>(
    [],
  );
  const scale = 0.01; // Scale factor for coordinates

  // Load zones data
  useEffect(() => {
    fetch("/zones-data.json")
      .then((res) => res.json())
      .then((data) => {
        if (data.data) {
          // Include all zones (including main corridors)
          setZones(data.data);
          console.log(`✅ Loaded ${data.data.length} zones`);
        }
      })
      .catch((err) => console.error("Failed to load zones:", err));

    // Load routing nodes
    fetch("/routing-nodes.json")
      .then((res) => res.json())
      .then((data) => {
        if (data.nodes) {
          setRoutingNodes(data.nodes);
          console.log(`✅ Loaded ${data.nodes.length} routing nodes`);
        }
      })
      .catch((err) => console.error("Failed to load routing nodes:", err));

    // Load POIs
    fetch("/pois-data.json")
      .then((res) => res.json())
      .then((data) => {
        if (data.data) {
          // Filter out RETAIL POIs as requested
          const filteredPois = data.data.filter(
            (poi: POI) => !poi.poiName.toLowerCase().includes("retail"),
          );
          setPois(filteredPois);
          console.log(
            `✅ Loaded ${filteredPois.length} POIs (filtered out retail)`,
          );
        }
      })
      .catch((err) => console.error("Failed to load POIs:", err));
  }, []);

  // Compute path when destination changes
  useEffect(() => {
    if (
      !selectedDestination ||
      routingNodes.length === 0 ||
      pois.length === 0
    ) {
      setComputedPath([]);
      return;
    }

    let startX: number, startY: number;

    // Use "SAB ATM - Gate 1" as the default starting point
    const startingPOI = pois.find((poi) => poi.poiName === "SAB ATM - Gate 1");

    if (startingPOI) {
      // Use SAB ATM - Gate 1 coordinates
      const points = startingPOI.points.split("#").map((point: string) => {
        const [x, y] = point.split(",").map(Number);
        return [x, y];
      });
      startX =
        points.reduce((sum: number, p: number[]) => sum + p[0], 0) /
        points.length;
      startY =
        points.reduce((sum: number, p: number[]) => sum + p[1], 0) /
        points.length;
      console.log(
        `🎯 Using SAB ATM - Gate 1 starting point at (${startX}, ${startY})`,
      );
    } else if (kioskData) {
      // Fallback to kioskData coordinates if SAB ATM not found
      startX = kioskData.coordinates?.x || 0;
      startY = kioskData.coordinates?.y || 0;
      console.log(
        `🎯 Fallback to kiosk starting point: ${kioskData.name} at (${startX}, ${startY})`,
      );
    } else {
      // No starting point available
      console.warn("⚠️ No starting point available");
      setComputedPath([]);
      return;
    }

    // Find matching POI for destination to get accurate coordinates
    const matchingPOI = pois.find(
      (poi) =>
        poi.poiName
          .toLowerCase()
          .includes(selectedDestination.name.toLowerCase()) ||
        selectedDestination.name
          .toLowerCase()
          .includes(poi.poiName.toLowerCase()),
    );

    let endX: number, endY: number;

    if (matchingPOI) {
      // Use POI coordinates (accurate)
      const points = matchingPOI.points.split("#").map((point: string) => {
        const [x, y] = point.split(",").map(Number);
        return [x, y];
      });
      endX =
        points.reduce((sum: number, p: number[]) => sum + p[0], 0) /
        points.length;
      endY =
        points.reduce((sum: number, p: number[]) => sum + p[1], 0) /
        points.length;
      console.log(
        `✅ Using POI coordinates for ${selectedDestination.name} -> ${matchingPOI.poiName} at (${endX}, ${endY})`,
      );
    } else {
      // Fallback to old coordinates if no POI match found
      endX = selectedDestination.coordinates?.x || 0;
      endY = selectedDestination.coordinates?.y || 0;
      console.log(
        `⚠️ No POI match found for ${selectedDestination.name}, using legacy coordinates at (${endX}, ${endY})`,
      );
    }

    const path = findPath(startX, startY, endX, endY, routingNodes);
    setComputedPath(path);

    const startingPointName = startingPOI
      ? "SAB ATM - Gate 1"
      : kioskData?.name || "Unknown";
    console.log(
      `✅ Computed path with ${path.length} waypoints from ${startingPointName} to ${selectedDestination.name}`,
    );
  }, [selectedDestination, kioskData, routingNodes, pois]);

  return (
    <div style={{ width: "100%", height: "100vh", background: "#f0f9ff" }}>
      <Canvas
        shadows
        camera={{ position: [50, 40, 50], fov: 50 }}
        style={{ background: "#f0f9ff" }}
      >
        {/* Lighting - reduced ambient light to prevent tinting */}
        <ambientLight intensity={0.3} />
        <directionalLight
          position={[50, 50, 25]}
          intensity={1.2}
          castShadow
          shadow-mapSize-width={2048}
          shadow-mapSize-height={2048}
        />
        <pointLight position={[-50, 30, -25]} intensity={0.6} />

        {/* Ground plane removed - no surrounding black base */}

        {/* Floor Plan Base Layer */}
        {/* TEMPORARILY HIDDEN - Uncomment to restore PNG floor plan */}
        {/* <FloorPlanBase scale={scale} /> */}

        {/* Unified Wall System - renders all walls with deduplication */}
        <UnifiedWallSystem zones={zones} scale={scale} />

        {/* Z10 Furniture - chairs, tables, and beds */}
        <Z10Furniture scale={scale} />

        {/* Render zone labels */}
        {zones.map((zone) => (
          <group key={zone.id}>
            <ZoneLabel zone={zone} scale={scale} />
          </group>
        ))}

        {/* Render POI icons with destination highlighting */}
        {pois.map((poi) => {
          // Check if this POI matches the selected destination by name
          const isSelectedDestination =
            selectedDestination &&
            (poi.poiName
              .toLowerCase()
              .includes(selectedDestination.name.toLowerCase()) ||
              selectedDestination.name
                .toLowerCase()
                .includes(poi.poiName.toLowerCase()));

          return (
            <group key={poi.id}>
              <POIIcon poi={poi} scale={scale} />

              {/* Add destination marker on top of matching POI */}
              {isSelectedDestination &&
                (() => {
                  const points = poi.points.split("#").map((point: string) => {
                    const [x, y] = point.split(",").map(Number);
                    return [x, y];
                  });
                  const centerX =
                    points.reduce((sum: number, p: number[]) => sum + p[0], 0) /
                    points.length;
                  const centerY =
                    points.reduce((sum: number, p: number[]) => sum + p[1], 0) /
                    points.length;
                  const [x, , z] = to3D(centerX, centerY, scale);

                  return (
                    <group position={[x, 0, z]}>
                      {/* Red pulsing destination marker */}
                      <PulsingDestinationMarker />
                    </group>
                  );
                })()}
            </group>
          );
        })}

        {/* Render starting point marker */}
        {pois.length > 0 && (
          <group>
            {(() => {
              // Always use SAB ATM - Gate 1 as starting point
              const startingPOI = pois.find(
                (poi) => poi.poiName === "SAB ATM - Gate 1",
              );

              if (startingPOI) {
                const points = startingPOI.points
                  .split("#")
                  .map((point: string) => {
                    const [x, y] = point.split(",").map(Number);
                    return [x, y];
                  });
                const centerX =
                  points.reduce((sum: number, p: number[]) => sum + p[0], 0) /
                  points.length;
                const centerY =
                  points.reduce((sum: number, p: number[]) => sum + p[1], 0) /
                  points.length;
                const [x, , z] = to3D(centerX, centerY, scale);

                return (
                  <group position={[x, 0, z]}>
                    {/* Starting point marker - green pin */}
                    <mesh position={[0, 2, 0]}>
                      <coneGeometry args={[0.3, 1, 8]} />
                      <meshStandardMaterial
                        color="#22c55e"
                        emissive="#22c55e"
                        emissiveIntensity={0.3}
                      />
                    </mesh>

                    {/* Starting point label */}
                    <Text
                      position={[0, 3.5, 0]}
                      fontSize={0.5}
                      color="#22c55e"
                      anchorX="center"
                      anchorY="middle"
                      maxWidth={startingPOI.poiName.length * 0.6}
                    >
                      📍 {startingPOI.poiName}
                    </Text>
                  </group>
                );
              }
              return null;
            })()}
          </group>
        )}

        {/* Starting point and POI markers are now the primary navigation elements */}

        {/* Old destination marker removed - now using POI-based highlighting above */}

        {/* Render animated route path */}
        {computedPath.length > 0 && (
          <AnimatedRoutePath path={computedPath} scale={scale} />
        )}

        {/* Controls */}
        <OrbitControls
          enableDamping
          dampingFactor={0.05}
          minDistance={10}
          maxDistance={150}
          maxPolarAngle={Math.PI / 2.1}
        />
      </Canvas>

      {/* Controls info */}
      <div
        style={{
          position: "absolute",
          bottom: 20,
          left: 20,
          background: "rgba(0, 0, 0, 0.7)",
          color: "white",
          padding: "12px",
          borderRadius: "8px",
          fontSize: "12px",
        }}
      >
        <div>🖱️ Left click + drag: Rotate</div>
        <div>🖱️ Right click + drag: Pan</div>
        <div>🖱️ Scroll: Zoom</div>
      </div>
    </div>
  );
};

export default Map3DZones;

